import { FormField } from '@/global/components/Form'
import { Label } from '@/global/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/global/components/ui/radio-group'
import { cn } from '@/lib/utils'
import { TrocaAutomaticaTabela } from '@/service/api'
import * as AccordionPrimitive from '@radix-ui/react-accordion'
import dayjs from 'dayjs'
import * as React from 'react'
import { useFormContext } from 'react-hook-form'

function Accordion({
    className,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root>) {
    return (
        <AccordionPrimitive.Root
            data-slot="accordion"
            className={cn(
                'rounded-lg shadow-md ring-1 ring-accent',
                'bg-card',
                'first:[&_div]:border-t-md',
                className,
            )}
            {...props}
        />
    )
}

function AccordionItem({
    className,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
    return (
        <AccordionPrimitive.Item
            data-slot="accordion-item"
            className={cn('pb-2 border-b last:border-b-0', className)}
            {...props}
        />
    )
}

function AccordionTrigger({
    className,
    children,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger>) {
    const [isOpen, setIsOpen] = React.useState(false)

    return (
        <AccordionPrimitive.Header className="flex">
            <AccordionPrimitive.Trigger
                data-slot="accordion-trigger"
                className={cn(
                    'focus-visible:border-ring focus-visible:ring-ring/50',
                    'flex flex-1 items-center justify-start',
                    'gap-4 text-left text-sm font-medium',
                    'transition-all outline-none',
                    'hover:bg-card-foreground/5 hover:cursor-pointer',
                    'focus-visible:ring-[3px]',
                    'disabled:pointer-events-none disabled:opacity-50',
                    'bg-inherit text-secondary-foreground p-4 border-0',
                    className,
                )}
                ref={el => {
                    if (el) {
                        const observer = new MutationObserver(() => {
                            setIsOpen(el.getAttribute('data-state') === 'open')
                        })
                        observer.observe(el, {
                            attributes: true,
                            attributeFilter: ['data-state'],
                        })
                        // Set initial state
                        setIsOpen(el.getAttribute('data-state') === 'open')
                        // return () => observer.disconnect()
                    }
                }}
                {...props}
            >
                <div
                    className={cn(
                        'size-4 rounded-full border-2 flex items-center justify-center transition-colors',
                        isOpen
                            ? 'border-blue-500 border-[2px]'
                            : 'border-gray-500/60',
                    )}
                >
                    {isOpen && (
                        <div className="size-2 rounded-full bg-blue-500" />
                    )}
                </div>
                {children}
            </AccordionPrimitive.Trigger>
        </AccordionPrimitive.Header>
    )
}

function AccordionContent({
    className,
    children,
    ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
    return (
        <AccordionPrimitive.Content
            data-slot="accordion-content"
            className={cn(
                'data-[state=closed]:animate-accordion-up',
                'data-[state=open]:animate-accordion-down',
                'overflow-hidden text-sm',
                'p-4',
            )}
            {...props}
        >
            <div className={cn('pt-0 pb-4', className)}>{children}</div>
        </AccordionPrimitive.Content>
    )
}

export default function AccordionRepetir() {
    const {
        watch,
        setValue,
        formState: { errors = {} },
    } = useFormContext<TrocaAutomaticaTabela>()

    const [
        dataInicial,
        tipoRepeticao,
        repeticaoSemanalDia,
        repeticaoMensalOpcao,
        every,
    ] = watch([
        'dataInicial',
        'tipoRepeticao',
        'repeticaoSemanalDia',
        'repeticaoMensalOpcao',
        'every',
    ])

    return (
        <Accordion
            type="single"
            className="w-full"
            value={tipoRepeticao}
            onValueChange={v => {
                if (v) setValue('tipoRepeticao', v as any)
            }}
        >
            {errors.tipoRepeticao && (
                <p className="text-red-500 text-xs">
                    {errors.tipoRepeticao.message}
                </p>
            )}
            <AccordionItem value="DIARIO">
                <AccordionTrigger>
                    A cada&nbsp;
                    <FieldEvery />
                    &nbsp;dia(s)
                    {tipoRepeticao === 'DIARIO' && errors.every && (
                        <p className="text-red-500 text-xs ml-2">
                            {errors.every.message}
                        </p>
                    )}
                </AccordionTrigger>
                <AccordionContent className="flex flex-col gap-4 text-balance">
                    {(() => {
                        const inicio = dayjs.utc(
                                dataInicial || dayjs.utc().format('YYYY-MM-DD'),
                            ),
                            everyValue = Number(every || 1)

                        return (
                            <p className="text-xs w-full">
                                Troca a cada {everyValue} dia
                                {everyValue < 2 ? '' : 's'} significa que a
                                primeira troca vai ocorrer em&nbsp;
                                {inicio.format('DD/MM/YYYY')}
                                &nbsp; e a segunda troca deve ocorrer no
                                dia&nbsp;
                                {inicio
                                    .add(everyValue + 1, 'day')
                                    .format('DD/MM/YYYY')}
                                .
                            </p>
                        )
                    })()}
                </AccordionContent>
            </AccordionItem>
            <AccordionItem value="SEMANAL">
                <AccordionTrigger>
                    A cada&nbsp;
                    <FieldEvery />
                    &nbsp;semana(s)
                    {tipoRepeticao === 'SEMANAL' && errors.every && (
                        <p className="text-red-500 text-xs ml-2">
                            {errors.every.message}
                        </p>
                    )}
                </AccordionTrigger>
                <AccordionContent className="flex flex-col gap-4 text-balance">
                    <RadioGroup
                        value={repeticaoSemanalDia.toString()}
                        onValueChange={v =>
                            setValue('repeticaoSemanalDia', parseInt(v))
                        }
                    >
                        <div className="flex gap-2 w-full justify-evenly">
                            {Array.from({ length: 7 }, (_, i) => (
                                <div key={i} className="flex items-center">
                                    <RadioGroupItem
                                        value={i.toString()}
                                        id={`day-${i}`}
                                        className="sr-only"
                                    />
                                    <Label
                                        htmlFor={`day-${i}`}
                                        className={cn(
                                            'cursor-pointer px-3 py-2 text-sm font-medium',
                                            'border-2 border-transparent rounded-full transition-all',
                                            'hover:bg-muted',
                                            repeticaoSemanalDia == i
                                                ? 'border-blue-500 text-blue-600'
                                                : i === 0
                                                  ? 'text-red-500'
                                                  : '',
                                        )}
                                    >
                                        {dayjs()
                                            .day(i)
                                            .format('ddd')
                                            .charAt(0)
                                            .toUpperCase() +
                                            dayjs()
                                                .day(i)
                                                .format('ddd')
                                                .slice(1)}
                                    </Label>
                                </div>
                            ))}
                        </div>
                        <p className="text-xs w-full">
                            A primeira troca deverá ocorrer no dia&nbsp;
                            <span className="font-semibold">
                                {(() => {
                                    const inicio = dayjs.utc(
                                        dataInicial ||
                                            dayjs.utc().format('YYYY-MM-DD'),
                                    )
                                    const data = inicio.day(repeticaoSemanalDia)
                                    return data.isBefore(inicio, 'day')
                                        ? inicio.format('DD/MM/YYYY')
                                        : data.format('DD/MM/YYYY')
                                })()}
                            </span>
                            .
                        </p>
                    </RadioGroup>
                </AccordionContent>
            </AccordionItem>
            <AccordionItem value="MENSAL">
                <AccordionTrigger>
                    A cada&nbsp;
                    <FieldEvery />
                    &nbsp;mes(es)
                    {tipoRepeticao === 'MENSAL' && errors.every && (
                        <p className="text-red-500 text-xs ml-2">
                            {errors.every.message}
                        </p>
                    )}
                </AccordionTrigger>
                <AccordionContent className="flex flex-col gap-4 text-balance">
                    <RadioGroup
                        value={repeticaoMensalOpcao}
                        onValueChange={v =>
                            setValue('repeticaoMensalOpcao', v as any)
                        }
                    >
                        <div className="flex flex-col gap-2 w-full justify-evenly">
                            <div>
                                <RadioGroupItem
                                    value="DIA"
                                    id="DIA"
                                    className="sr-only"
                                />
                                <Label
                                    htmlFor="DIA"
                                    className={cn(
                                        'cursor-pointer px-3 py-2 text-sm font-medium',
                                        'border-2 border-transparent rounded-full transition-all',
                                        'hover:bg-muted',
                                        repeticaoMensalOpcao === 'DIA'
                                            ? 'border-blue-500 text-blue-600'
                                            : '',
                                    )}
                                >
                                    Todo dia{' '}
                                    {dayjs
                                        .utc(
                                            dataInicial ||
                                                dayjs
                                                    .utc()
                                                    .format('YYYY-MM-DD'),
                                        )
                                        .date()}{' '}
                                    do mês
                                </Label>
                            </div>
                            <div>
                                <RadioGroupItem
                                    value="SEMANA"
                                    id="SEMANA"
                                    className="sr-only"
                                />
                                <Label
                                    htmlFor="SEMANA"
                                    className={cn(
                                        'cursor-pointer px-3 py-2 text-sm font-medium',
                                        'border-2 border-transparent rounded-full transition-all',
                                        'hover:bg-muted',
                                        repeticaoMensalOpcao === 'SEMANA'
                                            ? 'border-blue-500 text-blue-600'
                                            : '',
                                    )}
                                >
                                    <SpanWeekDay />
                                </Label>
                            </div>
                        </div>
                    </RadioGroup>
                    {repeticaoMensalOpcao === 'DIA' && (
                        <p className="text-xs w-full">
                            Se a troca for no dia 31 e o mês for fevereiro, com
                            apenas 28 dias, a troca será feita no dia 3 de
                            março. Se o mês tiver apenas 30 dias e a troca for
                            no dia 31, a troca será feita no dia 1º do próximo
                            mês.
                        </p>
                    )}
                    {repeticaoMensalOpcao === 'SEMANA' && (
                        <p className="text-xs w-full">
                            Se a troca for na 5ª semana e o mês não tiver o dia
                            específico na semana, a troca será feita na 1ª
                            semana do mês seguinte.
                        </p>
                    )}
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    )
}

function SpanWeekDay() {
    const { watch } = useFormContext<TrocaAutomaticaTabela>()
    const dataInicial = watch('dataInicial')

    const { ordinal, diaSemana, semana, artigo } =
        calcularRepeticao(dataInicial)

    return (
        <>
            Tod{artigo} {semana}
            {ordinal} {diaSemana} do mês
        </>
    )
}

function FieldEvery() {
    return (
        <FormField
            name="every"
            type="number"
            variant="standard"
            className="w-7 z-1 text-sm! text-center"
            onClick={e => e.stopPropagation()}
            min={0}
            max={999}
            placeholder="1"
            unHelperText
        />
    )
}

export function calcularRepeticao(dataInicial?: string | Date) {
    const dataInicialDayjs = dayjs.utc(
        dataInicial || dayjs.utc().format('YYYY-MM-DD'),
    )

    const indicadorOrdinal =
        dataInicialDayjs.day() === 0 || dataInicialDayjs.day() === 6 ? 'º' : 'ª'

    return {
        /* 'ª' ou 'º' */
        ordinal: indicadorOrdinal,
        /* Dia da semana em extenso */
        diaSemana: dataInicialDayjs.format('dddd'),
        /* Calcula qual semana do mês é baseado no dia da data */
        semana: Math.floor((dataInicialDayjs.date() - 1) / 7) + 1,
        /* 'o' ou 'a' */
        artigo: indicadorOrdinal === 'º' ? 'o' : 'a',
    }
}
